/*
  MyModel.jsx
  ----------------
  Interface pour afficher les modèles de l'utilisateur.
*/

import { useEffect, useState } from 'react';
import { get, post } from '../tools/Request';
import { useUser } from '../tools/Context';
import { Box, Text, Button, Select, NumberInput, Code, Divider, Group, Accordion, Table, Anchor, Modal, Tooltip, ActionIcon, CopyButton } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconCopy, IconCheck } from '@tabler/icons-react';

const BLOCKCHAIN_EXPLORER_ADDRESS = process.env.REACT_APP_BLOCKCHAIN_EXPLORER_ADDRESS;

export default function MyModel() {
  const [contracts, setContracts] = useState([]);
  const user = useUser();

  const [selectedContract, setSelectedContract] = useState('');
  const [selectedContractName, setSelectedContractName] = useState('');
  const [contractData, setContractData] = useState(null);

  const [maxHoursValue, setMaxHoursValue] = useState(0);
  const [contractAddress, setContractAddress] = useState('');

  const location = useLocation();
  const [autoSelected, setAutoSelected] = useState(false);
  const navigate = useNavigate();

  const [maxHoursModalOpen, setMaxHoursModalOpen] = useState(false);

  useEffect(() => {
    const fetchContracts = async () => {
      try {
        const response = await get('contract', undefined, localStorage.getItem('userToken'));
        setContracts(response);
      } catch (error) {
        console.error('Error fetching contract data:', error);
        setContracts([]);
      }
    };
    fetchContracts();
  }, [user]);

  useEffect(() => {
    if (!contracts.length || autoSelected) return;

    const params = new URLSearchParams(location.search);
    const selectedName = params.get('select');
    if (!selectedName) return;

    const exists = contracts.find((contract) => contract.name === selectedName);
    if (exists) {
      handleContractChange(selectedName);
      setAutoSelected(true);
    }
  }, [contracts, location.search, autoSelected]);

  useEffect(() => {
    const getSmartContract = async () => {
      if (!selectedContract) return;
      try {
        const res = await get('contract/code', { contract_id: selectedContract }, localStorage.getItem('userToken'));
        setContractData(res.content);
      } catch (e) {
        console.error('Error fetching contract code:', e);
      }
    };

    getSmartContract();
  }, [selectedContract]);

  const handleContractChange = (modelName) => {
    if (!modelName) {
      setSelectedContract('');
      setSelectedContractName('');
      setContractAddress('');
      setMaxHoursValue(0);
      setContractData(null);
      return;
    }

    const selected = contracts.find((contract) => contract.name === modelName);
    setSelectedContract(selected ? selected.id : '');
    setContractAddress(selected ? selected.contract_address : '');
    setSelectedContractName(modelName);
    setMaxHoursValue(selected ? selected.max_hours : 0);
  };

  const selectedContractObject = contracts.find((c) => c.id === selectedContract);
  const modelVariables = selectedContractObject?.variable?.variables || [];

  const goToDefineData = () => {
    if (selectedContractName) {
      navigate(`/definedata?select=${encodeURIComponent(selectedContractName)}`);
    } else {
      navigate('/definedata');
    }
  };

  const goToRetrieveData = () => {
    if (selectedContractName) {
      navigate(`/retrievedata?select=${encodeURIComponent(selectedContractName)}`);
    } else {
      navigate('/retrievedata');
    }
  };

  const saveMaxHours = async () => {
    try {
      await post(
        'contract/updateMaxHours',
        {
          contract_id: selectedContract,
          max_hours: maxHoursValue
        },
        localStorage.getItem('userToken')
      );

      showNotification({ title: 'Succès', message: 'Temps maximal mis à jour.', color: 'teal' });
      setMaxHoursModalOpen(false);
    } catch (error) {
      showNotification({
        title: 'Erreur',
        message: error.message || 'Une erreur est survenue.',
        color: 'red'
      });
    }
  };

  return (
    <>
      <Modal opened={maxHoursModalOpen} onClose={() => setMaxHoursModalOpen(false)} title="Modifier le délai maximal d'enregistrement" centered>
        <Text size="sm" c="dimmed" mb="sm">
          Temps maximal en heures avant qu’une attestation ne soit validée sur la blockchain.
        </Text>

        <NumberInput label="Temps maximal (heures)" value={maxHoursValue} onChange={setMaxHoursValue} min={1} max={168} suffix="h" />

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setMaxHoursModalOpen(false)}>
            Annuler
          </Button>
          <Button color="indigo" onClick={saveMaxHours} disabled={!selectedContract}>
            Sauvegarder
          </Button>
        </Group>
      </Modal>

      <Group mt={16}>
        <Select
          style={{ flex: 1 }}
          placeholder="Choisir un modèle"
          value={selectedContractName}
          onChange={handleContractChange}
          data={contracts.map((contract) => contract.name)}
        />
      </Group>

      <Group mt="sm" grow>
        <Button variant="light" onClick={goToDefineData}>
          Ajouter une attestation
        </Button>

        <Button variant="light" onClick={goToRetrieveData}>
          Récupérer des attestations
        </Button>
        {selectedContract && (
          <Button variant="light" onClick={() => setMaxHoursModalOpen(true)} disabled={!selectedContract}>
            Modifier le délai maximal
          </Button>
        )}
      </Group>

      {selectedContract && (
        <>
          <Box p="md" shadow="xs" withBorder>
            <Divider my="md" />

            <Text size="sm" c="dimmed" mb="sm">
              <strong>Créé le: </strong>{' '}
              {(() => {
                const c = selectedContractObject;
                if (!c?.creation_date) return '—';
                const timestamp = Number(c.creation_date);
                if (isNaN(timestamp)) return '—';
                return new Date(timestamp).toLocaleString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                });
              })()}
            </Text>
            <Group justify="space-between" align="center" mb="sm">
              <Text size="sm" c="dimmed">
                <strong>Identifiant du modèle :</strong> {selectedContract}
              </Text>

              <CopyButton value={selectedContract}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? 'Copié !' : 'Copier'} withArrow>
                    <ActionIcon color={copied ? 'teal' : 'gray'} variant="subtle" onClick={copy}>
                      {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            </Group>
            <Divider my="md" />

            <Text size="md" fw={500} mb="xs">
              Variables du modèle
            </Text>

            {modelVariables.length > 0 ? (
              <Table striped highlightOnHover withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Nom</Table.Th>
                    <Table.Th>Description</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Liste</Table.Th>
                    <Table.Th>Nom sur la blockchain</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {modelVariables.map((v, idx) => (
                    <Table.Tr key={idx}>
                      <Table.Td>{v.name}</Table.Td>
                      <Table.Td>{v.description}</Table.Td>
                      <Table.Td>{v.type}</Table.Td>
                      <Table.Td>{v.list ? 'Oui' : 'Non'}</Table.Td>
                      <Table.Td>
                        <Code>{v.variableName}</Code>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            ) : (
              <Text size="sm" c="dimmed">
                Aucune variable définie pour ce modèle.
              </Text>
            )}

            <Divider my="md" />

            <Accordion variant="separated">
              <Accordion.Item value="smart-contract-code">
                <Accordion.Control>Détails du smart contract</Accordion.Control>

                <Accordion.Panel>
                  <Text size="sm" c="dimmed" mb="sm">
                    <strong>Adresse du contrat :</strong>{' '}
                    <Anchor href={`${BLOCKCHAIN_EXPLORER_ADDRESS}${contractAddress}`} target="_blank" rel="noopener noreferrer">
                      {contractAddress}
                    </Anchor>
                  </Text>

                  {contractData ? <Code block>{contractData}</Code> : <Text>Récupération des données en cours...</Text>}
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          </Box>
        </>
      )}
    </>
  );
}
